import { useCallback, useState } from 'react'
import type {
  DataTablePaginationProps,
  DataTableSortDirection,
} from '@enamelai/design-system'

export const SETTINGS_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export type TableSort = {
  columnId?: string
  direction: DataTableSortDirection
}

export function sortRows<T extends Record<string, unknown>>(
  rows: T[],
  sort: TableSort,
  readValue: (row: T, columnId: string) => string = (row, columnId) =>
    String(row[columnId] ?? '').toLowerCase(),
): T[] {
  const { columnId, direction } = sort
  if (!columnId) return rows
  const factor = direction === 'desc' ? -1 : 1
  return [...rows].sort(
    (a, b) =>
      readValue(a, columnId).localeCompare(readValue(b, columnId)) * factor,
  )
}

/** Pass initialColumnId to open the table sorted A-Z on that column. */
export function useTableSort(
  initialColumnId?: string,
): [TableSort, (columnId: string) => void] {
  const [sort, setSort] = useState<TableSort>({
    columnId: initialColumnId,
    direction: 'asc',
  })
  const toggle = useCallback((columnId: string): void => {
    setSort((prev) =>
      prev.columnId === columnId
        ? { columnId, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { columnId, direction: 'asc' },
    )
  }, [])
  return [sort, toggle]
}

/** Client-side paging. Unless `alwaysShow`, pagination hides while one page fits. */
export function usePagedRows<T>(
  rows: T[],
  { alwaysShow = false }: { alwaysShow?: boolean } = {},
): { pageRows: T[]; pagination?: DataTablePaginationProps } {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(SETTINGS_PAGE_SIZE_OPTIONS[0])
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const safePage = Math.min(page, totalPages)

  if (!alwaysShow && rows.length <= SETTINGS_PAGE_SIZE_OPTIONS[0]) {
    return { pageRows: rows }
  }

  const start = (safePage - 1) * pageSize
  return {
    pageRows: rows.slice(start, start + pageSize),
    pagination: {
      currentPage: safePage,
      totalPages,
      pageSize,
      pageSizeOptions: SETTINGS_PAGE_SIZE_OPTIONS,
      onPageChange: setPage,
      onPageSizeChange: (size: number) => {
        setPageSize(size)
        setPage(1)
      },
    },
  }
}
